import { MethodChip } from "../../../design/components";
import { METHOD_BY_CONNECTION } from "../../../domain/health";
import { familyLabel, familyShort, type Family } from "../../../domain/families";
import type { Entity } from "../../../api/types";
import styles from "./mapping.module.css";
import { profileStateLine } from "./profileState";
import type { MappingCoverage, ProfileSummary } from "./types";

/** The family switcher; nothing when the company has one family, since the chip already says which. */
export function FamilyPicker({
  family,
  families,
  onSelect,
}: {
  family: Family;
  families: Family[];
  onSelect(family: Family): void;
}) {
  if (families.length <= 1) return null;
  return (
    <select
      className={styles.versionPick}
      aria-label="Document family"
      value={family}
      onChange={(event) => onSelect(event.target.value as Family)}
    >
      {families.map((item) => (
        <option key={item} value={item}>
          {familyLabel(item)}
        </option>
      ))}
    </select>
  );
}

export function ProfileHeader({
  entity,
  profile,
  family,
  families,
  version,
  coverage,
  readonly,
  dirtyCount,
  busy,
  onSelectFamily,
  onSelectVersion,
  onSave,
  onPublish,
  onDownload,
  onNewDraft,
}: {
  entity: Entity | undefined;
  profile: ProfileSummary;
  family: Family;
  /** The company's enabled families; the picker shows only when there is a choice. */
  families: Family[];
  version: string;
  coverage: MappingCoverage;
  readonly: boolean;
  dirtyCount: number;
  busy: boolean;
  onSelectFamily(family: Family): void;
  onSelectVersion(version: number): void;
  onSave(): void;
  onPublish(): void;
  onDownload(): void;
  onNewDraft(): void;
}) {
  const prefix = entity?.code ? `${entity.code} ` : "";

  return (
    <header className={styles.profileHead}>
      <div className={styles.profileWho}>
        <strong className={styles.profileName}>{entity?.legalName ?? profile.entityId}</strong>
        {entity?.crNumber && <span className="mono">CR {entity.crNumber}</span>}
        {entity && <MethodChip method={METHOD_BY_CONNECTION[entity.connectionMethod]} full />}
        <span className={styles.familyChip} aria-label="Document family">
          {familyShort(family)}
        </span>
        <span className={styles.profileScore}>
          Mapped {coverage.resolved}/{coverage.total}
        </span>
        <span className={styles.profileScore}>{profileStateLine(profile)}</span>
      </div>

      <div className={styles.profileActions}>
        <FamilyPicker family={family} families={families} onSelect={onSelectFamily} />
        <select
          className={styles.versionPick}
          aria-label="Mapping version"
          value={version}
          onChange={(event) => onSelectVersion(Number(event.target.value))}
        >
          {profile.versions.map((item) => (
            <option key={item.id} value={item.version}>
              {prefix}v{item.version} ({item.state})
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-sm"
          disabled={readonly || busy || dirtyCount === 0}
          onClick={onSave}
        >
          Save draft
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={readonly || busy}
          onClick={onPublish}
        >
          Publish new version
        </button>
        <details className={styles.overflow}>
          <summary aria-label="More actions" role="button">
            More
          </summary>
          <div className={styles.overflowMenu}>
            <button type="button" className="btn btn-sm btn-ghost" onClick={onDownload}>
              Download JSON
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={onNewDraft}>
              Start a new draft from this version
            </button>
          </div>
        </details>
      </div>

      {readonly && <p className={styles.note}>This version is published, so it is read-only.</p>}
    </header>
  );
}
